import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { useClient, useQuery } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAvoidingView from '@/components/Custom/ModalKeyboardAvoidingView';
import Paginator from '@/components/Custom/Paginator';
import * as Placeholder from '@/components/Custom/Placeholder';
import SearchBar from '@/components/Custom/SearchBar';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Button from '@/components/UI/Button';
import CheckboxRow from '@/components/UI/CheckboxRow';
import ContentBoundary from '@/components/UI/ContentBoundary';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import List from '@/components/Unistyles/List';
import { graphql, type ResultOf } from '@/gql/graphql';
import useConfig from '@/hooks/useConfig';
import { useDebouncedValue } from '@/hooks/useDebounce';
import useOffsetPagination from '@/hooks/useOffsetPagination';

const StyledSafeAreaView = withUnistyles(SafeAreaView);

export default function PersonManagers() {
  const { personId, teamId, teamTitle } = useLocalSearchParams<{
    personId: string;
    teamId?: string;
    teamTitle?: string;
  }>();
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetManagerEditor,
    variables: { personId, teamId: teamId ?? '' },
    pause: !teamId,
    requestPolicy: 'network-only',
  });
  const person = data?.person_by_pk;
  const team = data?.team[0];
  return (
    <ModalKeyboardAvoidingView>
      <StyledSafeAreaView style={styles.screen} edges={['bottom']}>
        <Stack.Screen
          options={{
            title: 'Managers',
            headerBackTitle: '',
            headerBackButtonDisplayMode: 'minimal',
            headerTitle: () => (
              <Box testID="ManagerSubject">
                <Text bold>Managers</Text>
                <Text size="xs" variant="typographySecondary" numberOfLines={1}>
                  {[person?.name, team?.title ?? teamTitle]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Box>
            ),
          }}
        />
        {!teamId ? (
          <Box p="lg">
            <Text>Open manager editing from a team membership.</Text>
          </Box>
        ) : (
          <ContentBoundary
            data={data}
            loading={fetching}
            error={error}
            errorMessage="Could not load managers."
            onRetry={() => refresh({ requestPolicy: 'network-only' })}
          >
            {(editor) =>
              editor.person_by_pk?.is_managed && editor.team.length ? (
                <ManagerEditor
                  key={`${personId}:${teamId}`}
                  personId={personId}
                  teamId={teamId}
                  managers={editor.person_manager}
                />
              ) : (
                <Box p="lg">
                  <Text>
                    Managers cannot be edited for this team membership.
                  </Text>
                </Box>
              )
            }
          </ContentBoundary>
        )}
      </StyledSafeAreaView>
    </ModalKeyboardAvoidingView>
  );
}

type ManagerCandidate = ResultOf<typeof GetManagerCandidates>['person'][number];

type ManagerSelection = { selectedPeople: ManagerCandidate[] };

function ManagerEditor({
  personId,
  teamId,
  managers,
}: {
  personId: string;
  teamId: string;
  managers: ResultOf<typeof GetManagerEditor>['person_manager'];
}) {
  const router = useRouter();
  const client = useClient();
  const config = useConfig();
  const [search, setSearch] = useState('');
  const { debouncedValue, setValue: debounceSearch } = useDebouncedValue('');
  const form = useForm<ManagerSelection>({
    defaultValues: {
      selectedPeople: managers.map(({ manager }) => manager),
    },
  });
  const selectedPeople = useWatch({
    control: form.control,
    name: 'selectedPeople',
  });
  const selectedIds = selectedPeople.map((person) => person.id);
  const { isSubmitting, errors, defaultValues } = form.formState;
  const originalIds =
    defaultValues?.selectedPeople?.flatMap((person) =>
      person?.id ? [person.id] : [],
    ) ?? [];
  const removedIds = originalIds.filter(
    (id): id is string => !!id && !selectedIds.includes(id),
  );
  const addedIds = selectedIds.filter((id) => !originalIds.includes(id));
  const changed = removedIds.length > 0 || addedIds.length > 0;
  const pagination = useOffsetPagination({ pageSize: 25 });
  const [candidates, refreshCandidates] = useQuery({
    query: GetManagerCandidates,
    variables: {
      where: {
        id: { _neq: personId },
        is_managed: { _eq: false },
        person_teams: {
          team: {
            id: { _eq: teamId },
            permissions: { _has_key: 'edit_person_manager' },
            team_people: { person_id: { _eq: personId } },
          },
        },
        name: { _ilike: `%${debouncedValue.trim()}%` },
      },
      offset: pagination.offset,
    },
    requestPolicy: 'cache-and-network',
  });
  const total = candidates.data?.person_aggregate.aggregate?.count ?? 0;
  const assigned = managers.map(({ manager }) => manager);
  const rows = [
    ...assigned.filter((manager) =>
      (manager.name ?? '')
        .toLowerCase()
        .includes(debouncedValue.trim().toLowerCase()),
    ),
    ...(candidates.data?.person ?? []).filter(
      (candidate) => !assigned.some((manager) => manager.id === candidate.id),
    ),
  ];
  const managerMax = Number.parseInt(
    config.person_team_person_manager_max ?? '',
    10,
  );
  const atLimit =
    Number.isFinite(managerMax) &&
    managerMax > 0 &&
    selectedIds.length >= managerMax;
  const submit = form.handleSubmit(async () => {
    if (!changed || isSubmitting) return;
    form.clearErrors('root');
    const latest = await client
      .query(
        GetManagerEditor,
        { personId, teamId },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    if (
      latest.error ||
      !latest.data?.person_by_pk?.is_managed ||
      !latest.data.team.length
    ) {
      form.setError('root.save', {
        message: 'Could not check managers. Try again.',
      });
      return;
    }
    const currentIds = latest.data.person_manager.map(
      (manager) => manager.manager_id,
    );
    const additions = addedIds.filter((id) => !currentIds.includes(id));
    const removals = removedIds.filter((id) => currentIds.includes(id));
    const count = currentIds.length - removals.length + additions.length;
    if (Number.isFinite(managerMax) && managerMax > 0 && count > managerMax) {
      form.setError('root.save', {
        message: `This team allows up to ${managerMax} managers.`,
      });
      return;
    }
    const result = await client
      .mutation(SavePersonManagers, {
        personId,
        teamId,
        removedIds: removals,
        added: additions.map((managerId) => ({
          person_id: personId,
          team_id: teamId,
          manager_id: managerId,
        })),
      })
      .toPromise();
    if (
      result.error ||
      result.data?.delete_person_manager?.affected_rows !== removals.length ||
      result.data?.insert_person_manager?.affected_rows !== additions.length
    ) {
      form.setError('root.save', {
        message:
          'Could not save all manager changes. Check your selections and try again.',
      });
      return;
    }
    router.back();
  });
  return (
    <FormProvider {...form}>
      <Box style={styles.searchContainer}>
        <SearchBar
          placeholder="Search people"
          accessibilityLabel="Search people"
          value={search}
          editable={!isSubmitting}
          onChangeText={(value) => {
            setSearch(value);
            debounceSearch(value);
            pagination.reset();
          }}
        >
          {selectedPeople.map((person) => (
            <Button
              key={person.id}
              variant="secondary"
              appearance="outline"
              shape="pill"
              disabled={isSubmitting}
              accessibilityLabel={`Unselect ${person.name ?? 'manager'}`}
              onPress={() => {
                form.clearErrors('root');
                form.setValue(
                  'selectedPeople',
                  selectedPeople.filter(
                    (selected) => selected.id !== person.id,
                  ),
                  { shouldDirty: true },
                );
              }}
            >
              <Box direction="horizontal" space="sm" align="center">
                <Avatar
                  size="xs"
                  name={person.name ?? ''}
                  color={person.color}
                />
                <Text flexShrink={1} numberOfLines={1} size="sm">
                  {person.name}
                </Text>
                <Icon name="close" size="md" />
              </Box>
            </Button>
          ))}
        </SearchBar>
      </Box>
      <List
        keyboardScroll={false}
        data={rows}
        fetching={candidates.fetching}
        extraData={[selectedIds, isSubmitting, atLimit]}
        keyExtractor={(item) => item.id}
        error={candidates.error}
        onRetry={() => refreshCandidates({ requestPolicy: 'network-only' })}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="person-outline" />
            <Placeholder.Text>No assignable managers here</Placeholder.Text>
          </Placeholder.Container>
        }
        renderItem={({ item }) => (
          <CheckboxRow
            role="checkbox"
            variant="info"
            value={selectedIds.includes(item.id)}
            accessibilityLabel={item.name ?? 'Manager'}
            testID={`ManagerCandidate-${item.id}`}
            disabled={
              isSubmitting || (!selectedIds.includes(item.id) && atLimit)
            }
            onValueChange={(checked) => {
              form.clearErrors('root');
              form.setValue(
                'selectedPeople',
                checked
                  ? [...selectedPeople, item]
                  : selectedPeople.filter((person) => person.id !== item.id),
                { shouldDirty: true },
              );
            }}
          >
            <PersonListItem.Container>
              <Avatar
                name={item.name ?? ''}
                id={item.avatar_id ?? ''}
                color={item.color}
                size="md"
              />
              <PersonListItem.Content>
                <Text numberOfLines={1} size="lg">
                  {item.name}
                </Text>
              </PersonListItem.Content>
            </PersonListItem.Container>
          </CheckboxRow>
        )}
        ListFooterComponent={
          total > pagination.limit ? (
            <Paginator {...pagination} total={total} />
          ) : null
        }
      />
      {!!errors.root?.save?.message && (
        <Box px="lg" pt="md">
          <Text role="alert" variant="error">
            {errors.root.save.message}
          </Text>
        </Box>
      )}
      <FormSaveButton
        info={{ text: 'Save', submittingText: 'Saving…' }}
        isDisabled={!changed || isSubmitting}
        onPress={() => void submit()}
      />
    </FormProvider>
  );
}

const styles = StyleSheet.create((theme) => ({
  screen: { flex: 1 },
  searchContainer: {
    paddingHorizontal: theme.size(4),
    paddingVertical: theme.size(2),
  },
}));

const GetManagerEditor = graphql(/* GraphQL */ `
  query GetManagerEditor($personId: String!, $teamId: uuid!) {
    team(
      where: {
        id: { _eq: $teamId }
        permissions: { _has_key: "edit_person_manager" }
        team_people: { person_id: { _eq: $personId } }
      }
      limit: 1
    ) {
      id
      title
    }
    person_by_pk(id: $personId) {
      id
      name
      avatar_id
      color
      is_managed
    }
    person_manager(
      where: { person_id: { _eq: $personId }, team_id: { _eq: $teamId } }
      order_by: [{ manager: { name: asc } }, { manager_id: asc }]
      limit: 100
    ) {
      person_id
      manager_id
      team_id
      manager {
        id
        name
        avatar_id
        color
      }
    }
  }
`);

const GetManagerCandidates = graphql(/* GraphQL */ `
  query GetManagerCandidates($where: person_bool_exp!, $offset: Int!) {
    person_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    person(
      where: $where
      order_by: [{ name: asc }, { id: asc }]
      limit: 25
      offset: $offset
    ) {
      id
      name
      avatar_id
      color
    }
  }
`);

const SavePersonManagers = graphql(/* GraphQL */ `
  mutation SavePersonManagers(
    $personId: String!
    $teamId: uuid!
    $removedIds: [String!]!
    $added: [person_manager_insert_input!]!
  ) {
    delete_person_manager(
      where: {
        person_id: { _eq: $personId }
        team_id: { _eq: $teamId }
        manager_id: { _in: $removedIds }
      }
    ) {
      affected_rows
    }
    insert_person_manager(objects: $added) {
      affected_rows
    }
  }
`);
